import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Save, CreditCard, ShieldCheck, AlertTriangle, Info, Tag, DollarSign } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface PaymentGatewaySettings {
  provider: string;
  test_mode: boolean;
  enabled: boolean;
}

const defaultSettings: PaymentGatewaySettings = {
  provider: 'stripe',
  test_mode: true,
  enabled: false,
};

export default function PaymentGatewayConfigTab() {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<PaymentGatewaySettings>(defaultSettings);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [stripePriceId, setStripePriceId] = useState('');
  const [stripeTestPriceId, setStripeTestPriceId] = useState('');
  const [priceAmount, setPriceAmount] = useState<string>('20');
  const [currency, setCurrency] = useState<string>('USD');
  const [billingCycle, setBillingCycle] = useState<string>('monthly');
  const [savingPrices, setSavingPrices] = useState(false);

  useEffect(() => {
    async function load() {
      const [settingsRes, planRes] = await Promise.all([
        supabase
          .from('platform_settings')
          .select('value')
          .eq('key', 'payment_gateway')
          .maybeSingle(),
        supabase
          .from('billing_plans')
          .select('stripe_price_id, stripe_test_price_id, price_amount, currency, billing_cycle')
          .eq('code', 'pro')
          .eq('active', true)
          .maybeSingle(),
      ]);
      if (settingsRes.data?.value) {
        setSettings({ ...defaultSettings, ...(settingsRes.data.value as any) });
      }
      if (planRes.data) {
        setStripePriceId(planRes.data.stripe_price_id || '');
        setStripeTestPriceId(planRes.data.stripe_test_price_id || '');
        setPriceAmount(planRes.data.price_amount != null ? String(planRes.data.price_amount) : '20');
        setCurrency(planRes.data.currency || 'USD');
        setBillingCycle(planRes.data.billing_cycle || 'monthly');
      }
      setLoaded(true);
    }
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await supabase
        .from('platform_settings')
        .upsert(
          {
            key: 'payment_gateway',
            value: settings as any,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'key' }
        );
      toast.success('Configuración de pagos guardada');
    } catch {
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePrices = async () => {
    setSavingPrices(true);
    try {
      const parsed = parseFloat(priceAmount);
      if (isNaN(parsed) || parsed < 0) {
        toast.error('Ingresa un precio válido');
        setSavingPrices(false);
        return;
      }
      const { error } = await supabase
        .from('billing_plans')
        .update({
          stripe_price_id: stripePriceId || null,
          stripe_test_price_id: stripeTestPriceId || null,
          price_amount: parsed,
          currency: currency as any,
          billing_cycle: billingCycle as any,
        })
        .eq('code', 'pro')
        .eq('active', true);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['license-plan-price'] });
      toast.success('Plan Pro guardado');
    } catch {
      toast.error('Error al guardar el plan');
    } finally {
      setSavingPrices(false);
    }
  };

  if (!loaded) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Pasarela de Pagos
          </CardTitle>
          <CardDescription>
            Configura la integración con Stripe para procesar pagos de suscripciones y certificaciones
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-border">
            <div>
              <p className="font-medium text-foreground">Pagos habilitados</p>
              <p className="text-sm text-muted-foreground">
                Activa para permitir pagos en la plataforma
              </p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, enabled: v }))}
            />
          </div>

          {/* Test mode */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              {settings.test_mode ? (
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              ) : (
                <ShieldCheck className="h-5 w-5 text-emerald-500" />
              )}
              <div>
                <p className="font-medium text-foreground">Modo de pruebas</p>
                <p className="text-sm text-muted-foreground">
                  {settings.test_mode
                    ? 'Los pagos se procesan en el entorno de pruebas de Stripe'
                    : 'Los pagos se procesan en producción — se cobrarán cargos reales'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                className={
                  settings.test_mode
                    ? 'bg-amber-500/20 text-amber-500 border-amber-500/30'
                    : 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30'
                }
              >
                {settings.test_mode ? 'Test' : 'Producción'}
              </Badge>
              <Switch
                checked={settings.test_mode}
                onCheckedChange={(v) => setSettings((s) => ({ ...s, test_mode: v }))}
              />
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar Configuración
          </Button>
        </CardContent>
      </Card>

      {/* Stripe Price IDs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Plan Pro — Precio
          </CardTitle>
          <CardDescription>
            Este monto se muestra en el perfil (tab Pagos) y en las landings (home, /planes). Asegúrate de que coincida con el precio configurado en Stripe.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="price-amount">Precio</Label>
              <Input
                id="price-amount"
                type="number"
                min="0"
                step="0.01"
                value={priceAmount}
                onChange={(e) => setPriceAmount(e.target.value)}
                placeholder="20"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="price-currency">Moneda</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger id="price-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="MXN">MXN</SelectItem>
                  <SelectItem value="COP">COP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="price-cycle">Ciclo</Label>
              <Select value={billingCycle} onValueChange={setBillingCycle}>
                <SelectTrigger id="price-cycle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensual</SelectItem>
                  <SelectItem value="annual">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stripe Price IDs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            IDs de Precio de Stripe (Plan Pro)
          </CardTitle>
          <CardDescription>
            Configura los IDs de precio de Stripe para producción y pruebas. Cada entorno de Stripe tiene sus propios IDs de precio.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="stripe-price-live">Price ID de Producción (price_...)</Label>
            <Input
              id="stripe-price-live"
              value={stripePriceId}
              onChange={(e) => setStripePriceId(e.target.value)}
              placeholder="price_1RVbAk..."
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">Se usa cuando el modo de pruebas está desactivado.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="stripe-price-test">Price ID de Pruebas (price_...)</Label>
            <Input
              id="stripe-price-test"
              value={stripeTestPriceId}
              onChange={(e) => setStripeTestPriceId(e.target.value)}
              placeholder="price_1ABC..."
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Se usa cuando el modo de pruebas está activado. Crea un precio recurrente en el Dashboard de Stripe (modo test) y pega el ID aquí.
            </p>
          </div>

          {settings.test_mode && !stripeTestPriceId && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm text-amber-600">
              ⚠️ El modo de pruebas está activo pero no hay un Price ID de pruebas configurado. Los pagos de licencia fallarán hasta que lo configures.
            </div>
          )}

          <Button onClick={handleSavePrices} disabled={savingPrices} className="gap-2">
            {savingPrices ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar Plan Pro
          </Button>
        </CardContent>
      </Card>

      {/* Secrets info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Credenciales de Stripe
          </CardTitle>
          <CardDescription>
            Las claves de Stripe se gestionan como secretos del backend para máxima seguridad
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-muted/50 border border-border space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-mono text-xs">STRIPE_SECRET_KEY</Badge>
              <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30 text-xs">Configurado</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Clave secreta de producción (sk_live_...) — Se usa cuando el modo de pruebas está desactivado.
            </p>
          </div>

          <div className="p-4 rounded-lg bg-muted/50 border border-border space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-mono text-xs">STRIPE_SECRET_KEY_TEST</Badge>
              <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30 text-xs">Configurado</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Clave secreta de pruebas (sk_test_...) — Se usa cuando el modo de pruebas está activado. Los pagos no generan cargos reales.
            </p>
          </div>

          <div className="p-4 rounded-lg bg-muted/50 border border-border space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-mono text-xs">STRIPE_WEBHOOK_SECRET</Badge>
              <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30 text-xs">Configurado</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              El secreto del webhook de Stripe (whsec_...) ya está configurado. Los eventos se verifican con firma criptográfica.
            </p>
          </div>

          <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-2">
            <p className="text-sm text-muted-foreground">
              <strong>URL del Webhook:</strong>
            </p>
            <code className="block bg-muted px-3 py-2 rounded text-xs break-all select-all">
              {import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-webhook
            </code>
            <p className="text-sm text-muted-foreground">
              En el Dashboard de Stripe → Developers → Webhooks, usa esta URL y selecciona los eventos: <code className="bg-muted px-1 py-0.5 rounded text-xs">checkout.session.completed</code>, <code className="bg-muted px-1 py-0.5 rounded text-xs">customer.subscription.updated</code>, <code className="bg-muted px-1 py-0.5 rounded text-xs">customer.subscription.deleted</code>, <code className="bg-muted px-1 py-0.5 rounded text-xs">invoice.payment_failed</code>.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
